export const MOVEMENT_ACTIONS = [
  'forward',
  'backward',
  'turnLeft',
  'turnRight',
  'strafeLeft',
  'strafeRight',
  'run',
  'ascend',
  'descend',
] as const;
export type MovementAction = (typeof MOVEMENT_ACTIONS)[number];
export type MovementBindings = Readonly<Record<MovementAction, string>>;
export interface MovementPreferences {
  readonly leftHanded: boolean;
  readonly bindings: MovementBindings;
}

const RIGHT: MovementBindings = {
  forward: 'KeyW',
  backward: 'KeyS',
  turnLeft: 'KeyA',
  turnRight: 'KeyD',
  strafeLeft: 'KeyQ',
  strafeRight: 'KeyE',
  run: 'ShiftLeft',
  ascend: 'Space',
  descend: 'KeyC',
};
const LEFT: MovementBindings = {
  forward: 'ArrowUp',
  backward: 'ArrowDown',
  turnLeft: 'ArrowLeft',
  turnRight: 'ArrowRight',
  strafeLeft: 'Delete',
  strafeRight: 'PageDown',
  run: 'ShiftRight',
  ascend: 'Space',
  descend: 'End',
};
const STORAGE_KEY = 'amsterdam1538-movement';
const RESERVED = new Set([
  'KeyB',
  'KeyF',
  'KeyG',
  'KeyH',
  'KeyJ',
  'KeyK',
  'KeyL',
  'KeyM',
  'KeyN',
  'KeyO',
  'KeyP',
  'KeyT',
  'KeyV',
]);

export function movementPreset(leftHanded: boolean): MovementPreferences {
  return { leftHanded, bindings: { ...(leftHanded ? LEFT : RIGHT) } };
}

export function isMovementKey(code: string): boolean {
  return (
    !RESERVED.has(code) &&
    /^(Key[A-Z]|Digit[0-9]|Numpad[0-9]|Arrow(Up|Down|Left|Right)|Space|Shift(Left|Right)|Delete|Insert|Home|End|PageUp|PageDown)$/.test(
      code
    )
  );
}

export function parseMovementPreferences(raw: string | null): MovementPreferences {
  if (!raw) return movementPreset(false);
  try {
    const value: unknown = JSON.parse(raw);
    if (
      !value ||
      typeof value !== 'object' ||
      !('leftHanded' in value) ||
      typeof value.leftHanded !== 'boolean' ||
      !('bindings' in value) ||
      !value.bindings ||
      typeof value.bindings !== 'object'
    )
      return movementPreset(false);
    const bindings = value.bindings as Record<string, unknown>;
    const keys = MOVEMENT_ACTIONS.map((action) => bindings[action]);
    if (
      keys.some((key) => typeof key !== 'string' || !isMovementKey(key)) ||
      new Set(keys).size !== keys.length
    )
      return movementPreset(false);
    return {
      leftHanded: value.leftHanded,
      bindings: Object.fromEntries(
        MOVEMENT_ACTIONS.map((action) => [action, bindings[action]])
      ) as MovementBindings,
    };
  } catch {
    return movementPreset(false);
  }
}

function storedPreferences(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

let preferences = parseMovementPreferences(storedPreferences());
export const movementPreferences = (): MovementPreferences => preferences;

export function saveMovementPreferences(next: MovementPreferences): boolean {
  preferences = parseMovementPreferences(JSON.stringify(next));
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
    return true;
  } catch {
    return false;
  }
}

export function movementKeyLabel(code: string): string {
  if (code.startsWith('Key')) return code.slice(3);
  if (code.startsWith('Digit')) return code.slice(5);
  const names: Readonly<Record<string, string>> = {
    ArrowUp: '↑',
    ArrowDown: '↓',
    ArrowLeft: '←',
    ArrowRight: '→',
    ShiftLeft: 'Left Shift',
    ShiftRight: 'Right Shift',
    PageDown: 'Page Down',
    PageUp: 'Page Up',
  };
  return names[code] ?? code;
}
